import {
  CaptionElement,
  FullResourceFragment,
} from "~/graphql/codegen/graphql";
import { useAnnotatePopoverConfiguration } from "~/components/annotate/hooks/use-annotate-popover-configuration";
import useAnnotationManager from "./hooks/annotation-manager";
import React, { useContext, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import {
  ProgressContext,
  useProgressReporterScrollListener,
} from "~/components/annotate/hooks/use-progress-repoter";
import { captureException } from "@sentry/react";
import { describeMediaError, describeMediaSource } from "./media-error";
import { MediaErrorPanel } from "./media-error-panel";
import metrics from "~/utils/metrics";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import AnnotateTriviaBar from "~/components/annotate/annotate-trivia-bar";
import { CurrentChunkClassName } from "./chunks/paragraph";
import { AudioContext } from "./hooks/audio-context";
import { VideoAnnotator } from "./video-annotator";
import { AnnotateCoachHighlights } from "./annotate-coach-highlights";
import { IHtmlHighlight } from "~/typings/annotate/annotation";
import { classNames, isFrontend } from "~/utils";
import { ResourceType } from "~/utils/resource-icon";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export function AnnotateViewMultimedia({
  article,
}: {
  article: FullResourceFragment;
}) {
  const { t } = useLingui();
  const popoverConfiguration = useAnnotatePopoverConfiguration(article);
  const [comments, highlights] = useAnnotationManager(
    article.id,
    article.annotations,
  );

  const playerRef = useRef<HTMLVideoElement>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState(
    article.userContext?.userProgress.lastTimestamp || 0,
  );
  // Which article+source pair has had its resume-on-ready step. Keyed on both
  // (not a boolean) so a change is seen synchronously by onReady, which
  // ReactPlayer fires from the media element's loadstart — possibly before a
  // passive effect could have reset a flag — and so two articles sharing one
  // media URL each resume from their own saved position. The player element
  // carries the same key, which forces a reload (and a fresh onReady) on an
  // in-place article swap; this component is not keyed by its parent.
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [autoScrollMode, setAutoScrollMode] = useState(true);
  const frontend = isFrontend();

  // Uploaded media carries a (signed) fileUrl and may have an empty url, so
  // fileUrl wins. Kept in one place so the player, the error panel and the
  // report all describe the same source.
  const mediaSrc = article.fileUrl ? `${article.fileUrl}` : article.url || "";
  const resumeKey = `${article.id}:${mediaSrc}`;
  const [playerError, setPlayerError] = useState<string | null>(null);
  const reportedErrorForSrc = useRef<string | null>(null);
  useEffect(() => {
    // A new source gets a clean slate (the component is reused across articles).
    setPlayerError(null);
  }, [mediaSrc]);
  useEffect(() => {
    // The saved timestamp belongs to the article, not the component instance.
    setCurrentTimestamp(article.userContext?.userProgress.lastTimestamp || 0);
  }, [article.id]);
  const handleProgress = (progress: any) => {
    setProgress({ videoTimestamp: progress.playedSeconds });
    // props.onVideoProgress(progress.playedSeconds);
    setCurrentTimestamp(progress.playedSeconds);
  };
  const setProgress = useContext(ProgressContext).setProgress;

  const transcriptRef = useRef<HTMLDivElement>(null);
  useProgressReporterScrollListener(
    transcriptRef as React.RefObject<HTMLDivElement>,
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const isPlaying = () => {
    if (!playerRef.current) {
      return false;
    }
    return (
      playerRef.current.currentTime > 0 &&
      !playerRef.current.paused &&
      !playerRef.current.ended &&
      playerRef.current.readyState > playerRef.current.HAVE_CURRENT_DATA
    );
  };

  useEffect(() => {
    if (autoScrollMode) {
      const activeEl = transcriptRef?.current?.getElementsByClassName(
        CurrentChunkClassName,
      );
      if (activeEl?.length) {
        activeEl[0].scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [currentTimestamp]);

  return (
    <div
      className={
        "h-full w-full flex flex-col overflow-auto pb-8 pt-8 space-y-12"
      }
    >
      <div
        className={classNames(
          "px-8 grow h-full w-full flex flex-col gap-4 overflow-hidden",
          article.type === ResourceType.VIDEO
            ? "lg:px-0 lg:grid lg:grid-cols-5 lg:grid-rows-2 "
            : "",
        )}
      >
        <AudioContext.Provider
          value={{
            currentTimestamp,
            seek: (v) => {
              if (playerRef.current) {
                playerRef.current.currentTime = v;
                setTimeout(() => {
                  if (!isPlaying()) {
                    playerRef.current?.play();
                  }
                }, 500);
              }
            },
          }}
        >
          <div
            className={classNames(
              "items-center justify-center h-fit max-h-[50%]",
              article.type === ResourceType.VIDEO
                ? "lg:ml-8 lg:col-span-3 lg:row-span-2 flex  lg:max-h-full lg:bg-black" +
                    " lg:h-full "
                : "",
            )}
          >
            <div
              className={classNames(
                article.type === ResourceType.VIDEO
                  ? "aspect-[16/9] w-full h-full lg:h-fit"
                  : "w-full mx-auto max-w-3xl",
              )}
            >
              {frontend && playerError ? (
                <MediaErrorPanel
                  mediaType={
                    article.type === ResourceType.VIDEO ? "video" : "audio"
                  }
                  sourceHref={mediaSrc || undefined}
                />
              ) : frontend ? (
                <ReactPlayer
                  key={resumeKey}
                  src={mediaSrc}
                  onReady={() => {
                    if (initializedFor !== resumeKey) {
                      // Resume from the article's own saved position, read
                      // directly rather than from state that may still hold
                      // the previous article's value.
                      const resumeAt =
                        article.userContext?.userProgress.lastTimestamp || 0;
                      if (resumeAt > 1 && playerRef.current) {
                        playerRef.current.currentTime = resumeAt;
                      }
                      setInitializedFor(resumeKey);
                    }
                  }}
                  controls={true}
                  width={"100%"}
                  height={
                    article.type === ResourceType.VIDEO ? "100%" : "100px"
                  }
                  ref={playerRef}
                  onProgress={handleProgress}
                  onEnded={() => {
                    // setHasEnded(true);
                  }}
                  onError={(event) => {
                    const detail = describeMediaError(event);
                    const source = describeMediaSource(article);
                    console.error("Player error:", detail, source);
                    // Once per source per mount: a broken source can fire
                    // several times while the browser gives up.
                    if (reportedErrorForSrc.current !== mediaSrc) {
                      reportedErrorForSrc.current = mediaSrc;
                      captureException(
                        new Error(`Media player error: ${detail.message}`),
                        {
                          tags: {
                            articleId: article.id,
                            mediaType:
                              article.type === ResourceType.VIDEO
                                ? "video"
                                : "audio",
                            mediaSourceKind: source.kind,
                            mediaSourceHost: source.host,
                            mediaErrorCode: String(detail.code ?? "unknown"),
                          },
                          contexts: {
                            media: {
                              // origin + path only; a signed fileUrl's query
                              // string carries credentials
                              source: source.location,
                              errorCode: detail.code,
                              errorMessage: detail.message,
                            },
                          },
                        },
                      );
                    }
                    setPlayerError(detail.message);
                  }}
                />
              ) : null}
            </div>
          </div>
          <div
            className={classNames(
              "col-span-5  pb-6 h-full overflow-auto pr-16",
              article.type === ResourceType.VIDEO
                ? "lg:mr-0 lg:col-span-2 lg:row-span-2"
                : "",
            )}
            onWheel={() => setAutoScrollMode(false)}
            ref={transcriptRef}
          >
            <div className="w-full flex flex-col space-y-6 max-w-3xl mx-auto">
              <h2
                className={classNames(
                  "shrink-0",
                  article.type !== ResourceType.VIDEO ? "mx-auto" : "",
                )}
              >
                {article.headline}
              </h2>
              <AnnotateCoachHighlights article={article} />
              <VideoAnnotator
                htmlContent={article.editorContent || ""}
                popoverConfiguration={popoverConfiguration}
                annotations={{
                  comments,
                  highlights: highlights as IHtmlHighlight[],
                }}
                captions={(article.caption?.caption || []) as CaptionElement[]}
                trackMetric={metrics.track}
                articleID={article.id}
                investigatorInfo={{
                  ref: panelRef as React.RefObject<HTMLDivElement>,
                  visible: false,
                }}
                endElement={<p></p>}
              />
              {(article.caption?.state === "error" ||
                !article.caption?.caption) && (
                <p className="text-center p-4 md:p-0">
                  <Trans>Sorry - no transcript available for this resource.</Trans>
                </p>
              )}
              {article.caption?.state === "loading" && (
                <p className=" p-4 md:p-0">
                  <Trans>
                    We are still generating the transcript, but it will appear
                    automatically when it's ready.
                  </Trans>
                  <br /> <Trans>It can take a few minutes to appear.</Trans>
                </p>
              )}
              <AnnotateTriviaBar resource={article} />
              {!autoScrollMode && (
                <div
                  className={classNames(
                    "fixed flex flex-col-reverse  justify-end z-5 max-h-0 right-6 " +
                      " transition-all bottom-[150px] drop-shadow-md",
                  )}
                >
                  <div className="text-end mb-4 md:mb-0 h-auto">
                    <button
                      className="hover:[&>svg]:bg-muted/80"
                      onClick={() => {
                        //TODO:
                        setAutoScrollMode(true);
                      }}
                      title={t(msg`Scroll To Current`)}
                    >
                      <ChevronUpDownIcon className="w-10 h-10 p-1.5 text-foreground bg-muted rounded-full" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </AudioContext.Provider>
      </div>
    </div>
  );
}
